import { useEffect } from 'react';
import { db } from '../firebase';
import { doc, setDoc, updateDoc, increment, collection, addDoc } from 'firebase/firestore';
import { useLocation } from 'react-router-dom';

interface VisitorData {
  userAgent: string;
  language: string;
  referrer: string;
  screen: {
    width: number;
    height: number;
  };
  timestamp: Date;
  sessionId: string;
  isNewVisitor: boolean;
}

interface PageView {
  path: string;
  timestamp: Date;
  sessionId: string;
  userId?: string;
  referrer: string;
  timeOnPage?: number;
}

// Generate session ID
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get or create visitor ID
const getVisitorId = (): string => {
  let visitorId = localStorage.getItem('analytics_visitor_id');
  if (!visitorId) {
    visitorId = Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('analytics_visitor_id', visitorId);
  }
  return visitorId;
};

// Track page view
const trackPageView = async (path: string, userId?: string) => {
  try {
    const sessionId = getSessionId();
    const visitorId = getVisitorId();
    
    // Build page view data without undefined values
    const pageViewData: any = {
      path,
      timestamp: new Date(),
      sessionId,
      referrer: document.referrer,
      visitorId
    };
    
    // Only add userId if it's defined (Firestore doesn't allow undefined values)
    if (userId) {
      pageViewData.userId = userId;
    }
    
    await addDoc(collection(db, 'analytics', 'data', 'pageViews'), pageViewData);

    // Update daily stats
    const today = new Date().toISOString().split('T')[0];
    const dailyStatsRef = doc(db, 'analytics', 'stats', 'daily', today);
    
    await setDoc(dailyStatsRef, {
      date: today,
      pageViews: increment(1),
      uniquePaths: {},
      updatedAt: new Date()
    }, { merge: true });

    // Update path-specific stats
    await updateDoc(dailyStatsRef, {
      [`uniquePaths.${path.replace(/\//g, '_')}`]: increment(1)
    });

  } catch (error) {
    console.warn('Analytics tracking failed:', error);
  }
};

// Track visitor
const trackVisitor = async (userId?: string) => {
  try {
    const sessionId = getSessionId();
    const visitorId = getVisitorId();
    const today = new Date().toISOString().split('T')[0];

    // Check if this is a new session
    const isNewSession = !sessionStorage.getItem('analytics_session_tracked');
    
    if (isNewSession) {
      sessionStorage.setItem('analytics_session_tracked', 'true');
      
      const visitorData: VisitorData = {
        userAgent: navigator.userAgent,
        language: navigator.language,
        referrer: document.referrer,
        screen: {
          width: window.screen.width,
          height: window.screen.height
        },
        timestamp: new Date(),
        sessionId,
        isNewVisitor: !localStorage.getItem('analytics_returning_visitor')
      };

      // Mark as returning visitor
      localStorage.setItem('analytics_returning_visitor', 'true');

      // Add to visitors collection - only include userId if defined
      const visitorDocData: any = {
        ...visitorData,
        visitorId
      };
      
      // Only add userId if it's defined
      if (userId) {
        visitorDocData.userId = userId;
      }
      
      await addDoc(collection(db, 'analytics', 'data', 'visitors'), visitorDocData);

      // Update daily visitor stats
      const dailyStatsRef = doc(db, 'analytics', 'stats', 'daily', today);
      await setDoc(dailyStatsRef, {
        date: today,
        visitors: increment(1),
        newVisitors: visitorData.isNewVisitor ? increment(1) : increment(0),
        sessions: increment(1),
        updatedAt: new Date()
      }, { merge: true });
    }
  } catch (error) {
    console.warn('Visitor tracking failed:', error);
  }
};

// Custom hook
const useAnalytics = (userId?: string) => {
  const location = useLocation();

  useEffect(() => {
    // Track visitor on first load
    trackVisitor(userId);
  }, [userId]);

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname, userId);
  }, [location.pathname, userId]);

  // Return tracking functions for manual use
  return {
    trackEvent: async (eventName: string, properties?: Record<string, any>) => {
      try {
        const eventData: any = {
          eventName,
          properties,
          timestamp: new Date(),
          sessionId: getSessionId(),
          visitorId: getVisitorId(),
          path: location.pathname
        };
        
        // Only add userId if it's defined
        if (userId) {
          eventData.userId = userId;
        }
        
        await addDoc(collection(db, 'analytics', 'data', 'events'), eventData);
      } catch (error) {
        console.warn('Event tracking failed:', error);
      }
    },
    trackConversion: async (type: string, value?: number) => {
      try {
        const conversionData: any = {
          type,
          value,
          timestamp: new Date(),
          sessionId: getSessionId(),
          visitorId: getVisitorId(),
          path: location.pathname
        };
        
        // Only add userId if it's defined
        if (userId) {
          conversionData.userId = userId;
        }
        
        await addDoc(collection(db, 'analytics', 'data', 'conversions'), conversionData);
      } catch (error) {
        console.warn('Conversion tracking failed:', error);
      }
    }
  };
};

export default useAnalytics; 